// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { X, Check, AlertCircle, Eye, EyeOff, Shield, Mail } from 'lucide-react';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
  redirectTo?: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'login', redirectTo = '/overview' }) => {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength | null>(null);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Reset form when switching modes
  useEffect(() => {
    setError('');
    setAgreedToTerms(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [mode]);

  // Calculate password strength
  useEffect(() => {
    if (mode === 'register' && password) {
      const checks = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
      };

      const score = Object.values(checks).filter(Boolean).length;

      let label = '';
      let color = '';
      if (score === 0) {
        label = '';
        color = '';
      } else if (score <= 2) {
        label = 'Weak';
        color = 'red';
      } else if (score === 3) {
        label = 'Fair';
        color = 'yellow';
      } else if (score === 4) {
        label = 'Good';
        color = 'blue';
      } else {
        label = 'Strong';
        color = 'green';
      }

      setPasswordStrength({ score, label, color, checks });
    } else {
      setPasswordStrength(null);
    }
  }, [password, mode]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'register') {
      // Validate password strength
      if (!passwordStrength || passwordStrength.score < 3) {
        setError('Please choose a stronger password');
        return;
      }

      // Validate password match
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      // Validate terms agreement
      if (!agreedToTerms) {
        setError('Please accept the Terms of Service and Privacy Policy');
        return;
      }
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        await new Promise(resolve => setTimeout(resolve, 100));
        navigate(redirectTo);
        onClose();
      } else {
        await register(email, password);
        await new Promise(resolve => setTimeout(resolve, 100));
        // Show email verification message
        setError('');
        navigate(redirectTo);
        onClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || `${mode === 'login' ? 'Login' : 'Registration'} failed`);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  const getStrengthBarColor = () => {
    if (!passwordStrength) return 'bg-gray-200';
    switch (passwordStrength.color) {
      case 'red': return 'bg-red-500';
      case 'yellow': return 'bg-yellow-500';
      case 'blue': return 'bg-blue-500';
      case 'green': return 'bg-emerald-500';
      default: return 'bg-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`relative w-full max-w-md rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${
        isDark ? 'bg-zinc-900 border border-white/10' : 'bg-white border border-gray-200'
      }`}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-2 rounded-lg transition-colors z-10 ${
            isDark ? 'hover:bg-white/10 text-white/70 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
          }`}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Header with Security Badge */}
          <div className="flex items-start gap-3 mb-4">
            <div className={`p-2 rounded-lg ${
              isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'
            }`}>
              <Shield className={`h-6 w-6 ${
                isDark ? 'text-emerald-400' : 'text-emerald-600'
              }`} />
            </div>
            <div className="flex-1">
              <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {mode === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className={`text-sm mt-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                {mode === 'login' ? 'Sign in to continue to InvestVerse.AI' : 'Join InvestVerse.AI with secure registration'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            {error && (
              <div className={`px-4 py-3 rounded-lg border flex items-start gap-2 ${
                isDark
                  ? 'bg-red-500/10 border-red-500/30 text-red-400'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                Email Address
              </label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
                  isDark ? 'text-white/40' : 'text-gray-400'
                }`} />
                <input
                  type="email"
                  required
                  className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder-white/40'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={mode === 'register' ? 8 : undefined}
                  className={`w-full px-4 py-3 pr-10 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder-white/40'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors ${
                    isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'
                  }`}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {mode === 'register' && password && passwordStrength && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${
                      isDark ? 'text-white/70' : 'text-gray-700'
                    }`}>
                      Password Strength: <span className={`text-${passwordStrength.color}-500`}>{passwordStrength.label}</span>
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          i < passwordStrength.score ? getStrengthBarColor() : isDark ? 'bg-white/10' : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(passwordStrength.checks).map(([key, value]) => (
                      <div key={key} className={`flex items-center gap-1 ${
                        value ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-white/40' : 'text-gray-400')
                      }`}>
                        {value ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        <span>
                          {key === 'length' && '8+ characters'}
                          {key === 'uppercase' && 'Uppercase'}
                          {key === 'lowercase' && 'Lowercase'}
                          {key === 'number' && 'Number'}
                          {key === 'special' && 'Special char'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            {mode === 'register' && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    className={`w-full px-4 py-3 pr-10 rounded-lg border focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${
                      isDark
                        ? 'bg-white/5 border-white/10 text-white placeholder-white/40'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded transition-colors ${
                      isDark ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-500'
                    }`}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs mt-1.5 text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Passwords do not match
                  </p>
                )}
                {confirmPassword && password === confirmPassword && (
                  <p className={`text-xs mt-1.5 flex items-center gap-1 ${
                    isDark ? 'text-emerald-400' : 'text-emerald-600'
                  }`}>
                    <Check className="h-3 w-3" />
                    Passwords match
                  </p>
                )}
              </div>
            )}

            {/* Terms and Conditions Checkbox */}
            {mode === 'register' && (
              <div className={`p-4 rounded-lg border ${
                isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'
              }`}>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    required
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                  <span className={`text-sm flex-1 ${
                    isDark ? 'text-white/70 group-hover:text-white/90' : 'text-gray-600 group-hover:text-gray-900'
                  } transition-colors`}>
                    I agree to the{' '}
                    <a href="/terms" target="_blank" className={`font-semibold ${
                      isDark ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'
                    } underline`}>
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a href="/privacy" target="_blank" className={`font-semibold ${
                      isDark ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'
                    } underline`}>
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || (mode === 'register' && !agreedToTerms)}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                isDark
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600 disabled:bg-emerald-500/50 disabled:cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{mode === 'login' ? 'Signing in...' : 'Creating account...'}</span>
                </>
              ) : (
                <>
                  {mode === 'register' && <Shield className="h-4 w-4" />}
                  <span>{mode === 'login' ? 'Sign In' : 'Create Secure Account'}</span>
                </>
              )}
            </button>
          </form>

          {/* Switch Mode */}
          <div className="mt-6 text-center">
            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                onClick={switchMode}
                className={`font-semibold transition-colors ${
                  isDark ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'
                }`}
              >
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>

          {/* Security Badge */}
          {mode === 'register' && (
            <div className={`mt-6 p-3 rounded-lg flex items-center gap-2 text-xs ${
              isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-700'
            }`}>
              <Shield className="h-4 w-4 flex-shrink-0" />
              <span>Your data is encrypted and secure. We never share your information.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
